import { Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Fund } from '../../data/types';
import { distributionTotals } from '../../domain/distributionCalc';
import { date, money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { SectionCard, StatusChip } from '../../ui/primitives';

interface EventRow {
  id: string;
  kind: 'Capital call' | 'Distribution';
  label: string;
  date: string;
  amount: number;
  status: string;
  to: string;
}

export default function FundEventsTab({ fund }: { fund: Fund }) {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const rows = useMemo<EventRow[]>(() => {
    const calls = scope.calls
      .filter((call) => call.fundId === fund.id)
      .map((call) => ({
        id: call.id,
        kind: 'Capital call' as const,
        label: `Call ${call.number} (${call.callPct}%) · ${call.purpose}`,
        date: call.dueDate,
        amount: sumBy(call.lines, (line) => line.amount),
        status: call.status,
        to: `/manager/calls/${call.id}`,
      }));
    const distributions = scope.distributions
      .filter((distribution) => distribution.fundId === fund.id)
      .map((distribution) => ({
        id: distribution.id,
        kind: 'Distribution' as const,
        label: `${scope.classById.get(distribution.classId)?.name} · period ending ${date(distribution.periodEnd)} · ${distribution.centsPerUnit} cpu`,
        date: distribution.paymentDate,
        amount: distributionTotals(distribution).gross,
        status: distribution.status,
        to: `/manager/distributions/${distribution.id}`,
      }));
    return [...calls, ...distributions];
  }, [scope, fund.id]);

  return (
    <SectionCard
      noPadding
      title="Calls & distributions"
      subtitle="Payment events for this fund, newest first"
    >
      <DataTable
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(row.to)}
        defaultSort={{ key: 'date', direction: 'desc' }}
        dense
        columns={[
          {
            key: 'kind',
            label: 'Event',
            sortValue: (row) => row.kind,
            render: (row) => (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.kind}
              </Typography>
            ),
          },
          { key: 'label', label: 'Detail', render: (row) => row.label },
          {
            key: 'date',
            label: 'Due / payable',
            sortValue: (row) => row.date,
            render: (row) => date(row.date),
          },
          {
            key: 'amount',
            label: 'Gross amount',
            align: 'right',
            sortValue: (row) => row.amount,
            render: (row) => money(row.amount),
          },
          { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
        ]}
        emptyMessage="No calls or distributions yet"
      />
    </SectionCard>
  );
}
